import { Settings } from "../config";
import {
  FetchStatus,
  LanderCookie,
  QueryParam,
  System,
} from "../commons/Constants";
import { FeedType } from "../types/LanderConfig";
import { abpParams } from "../adblock/constants";
import { YahooMetaData } from "../types/YahooMetaData";
import {
  getCookie,
  objectToQueryStringExcludeNull,
} from "../commons/HelperFunctions";
import Logger from "../commons/Logger";

function customerIdToTypeTag(customerId: string | undefined): string {
  if (!customerId) {
    return "missingTag";
  }
  return customerId.replace(/-/g, "");
}

export function getYahooRSUri({
  rootDomain,
  keywords,
  system,
  customerId,
  typeTag,
}: {
  rootDomain: string;
  keywords: string[];
  system: string;
  customerId?: string;
  typeTag?: string;
}) {
  let partner = "";
  // if test partner is set, override other config
  if (Settings.YAHOO_FEED_PARTNER.TEST) {
    partner = Settings.YAHOO_FEED_PARTNER.TEST;
  } else {
    partner = Settings.YAHOO_SINGLE_LANDER_PARTNER.SEARCH_DOMAIN_PARKING;
  }

  let type = "";
  if (system === System.PARKWEB) {
    type = typeTag || System.PARKWEB;
  } else {
    type = customerIdToTypeTag(customerId);
  }

  const rsParams: any = {
    url: rootDomain,
    Partner: partner,
    maxListings: 3,
    maxWeb: 0,
    maxRT: 6,
    maxRTL: 6,
    maxPC: 6,
    urlLang: "en",
    type: type,
    affilData: {
      ip: getCookie(LanderCookie.CLIENT_IP),
      xfip: getCookie(LanderCookie.CLIENT_IP),
      ua: navigator.userAgent,
      ur: window.location.href.replace(window.location.search, ""), // Strip query params
    },
    serveUrl: `${window.location.protocol}//${rootDomain}`,
  };
  if (keywords) rsParams.seedTerm = keywords.join(" ");

  return `?${objectToQueryStringExcludeNull(rsParams)}`;
}

export function getYahooAdsUri({
  qs,
  rootDomain,
  searchQuery,
  system,
  customerId,
  typeTag,
}: {
  qs: string;
  rootDomain: string;
  searchQuery: string;
  system?: string | undefined;
  customerId?: string;
  typeTag?: string;
}) {
  // Get url params
  const newURL = new URLSearchParams(window.location.search);
  // Ensure we don't have duplicate url params
  newURL.delete("qs");
  newURL.delete("q");
  newURL.delete("serveURL");
  newURL.delete("domain");
  newURL.delete(QueryParam.FEED_ID);

  if (searchQuery) newURL.append("q", searchQuery);
  newURL.append("serveURL", `${window.location.protocol}//${rootDomain}`);

  // System and domain are important here since this uri will take the user to
  // the single lander hostname and show the yahoo ads.
  // The yahoo single lander will need these params to show the correct lander experience.
  if (system) newURL.append("system", system);
  newURL.append("domain", rootDomain);
  // Hardcode feedid=2 (YAHOO) to prevent RSOC redirect loops when returning from content site
  newURL.append(QueryParam.FEED_ID, FeedType.YAHOO.toString());

  let type = "";
  if (system === System.PARKWEB) {
    type = typeTag || System.PARKWEB;
  } else {
    type = customerIdToTypeTag(customerId);
  }
  newURL.append("type", type);

  const url = new URL(window.location.href);
  url.search = newURL.toString();
  url.search += `&${qs}`;

  url.hostname = Settings.YAHOO_SINGLE_LANDER_HOSTNAME;

  return url.toString();
}

export async function yahooXmlFetch(urlParams: string) {
  try {
    const response = await fetch(
      `${Settings.YAHOO_FEED_PATH}${urlParams}&${abpParams}`,
      {
        method: "GET",
        credentials: "omit",
      }
    );

    if (response.status !== 200)
      throw new Error(`got ${response.status} from yahoo`);

    const txt = await response.text();
    const xml = new DOMParser().parseFromString(txt, "text/xml");
    if (xml.querySelector("parsererror"))
      throw new Error("xml failed to parse");

    // Check for errors from Yahoo Feed
    Array.from(xml.getElementsByTagName("StatusItem")).forEach((statusItem) => {
      const status = parseInt(statusItem.getAttribute("code"), 10);
      if (status >= 400)
        throw new Error(
          `${status} response from yahoo feed: ${statusItem.textContent}`
        );
    });

    return {
      fetchStatus: FetchStatus.SUCCESS,
      xmlResult: xml,
    };
  } catch (err) {
    Logger.error(err);
    return {
      fetchStatus: FetchStatus.FAILURE,
      message: err.message,
    };
  }
}

export function extractYahooMetaData(
  xmlResult: Document
): YahooMetaData | null {
  const metaData = xmlResult.getElementsByTagName("MetaData")[0];

  if (!metaData) {
    Logger.warn("MetaData element not found in Yahoo XML response");
    return null;
  }

  return {
    searchId: metaData.getElementsByTagName("SearchID")[0]?.textContent || "",
    clientId: metaData.getElementsByTagName("ClientID")[0]?.textContent || "",
    impressionGuid:
      metaData.getElementsByTagName("ImpressionGUID")[0]?.textContent || "",
    rguid: metaData.getElementsByTagName("RGUId")[0]?.textContent || "",
  };
}
